import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { PlusCircle, ArrowRight, Info, Star, Menu, ChevronRight, X, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './AvailablePlans_mobile.css';

// Haptic feedback utility (simulated for 2025 touch devices)
const triggerHaptic = (intensity = 'medium') => {
  if (window.navigator && window.navigator.vibrate) {
    switch(intensity) {
      case 'light':
        window.navigator.vibrate(5);
        break;
      case 'medium':
        window.navigator.vibrate(10);
        break;
      case 'strong':
        window.navigator.vibrate([5, 10, 15]);
        break;
      default:
        window.navigator.vibrate(10);
    }
  }
};

// Enhanced Mobile Carrier Card
const MobileCarrierCard = ({ carrier, index, isActive, onActivate, isFocused }) => {
  const [animateGlow, setAnimateGlow] = useState(false);
  const cardRef = useRef(null);
  
  // For card tilt effect based on device gyroscope
  const [tiltX, setTiltX] = useState(0);
  const [tiltY, setTiltY] = useState(0);
  
  useEffect(() => {
    // Glow effect animation timer
    const timer = setTimeout(() => {
      setAnimateGlow(true);
    }, 300 + index * 100);
    
    // Device orientation for 3D tilt effect
    const handleOrientation = (event) => {
      if (event.gamma && event.beta && isActive) {
        setTiltX(event.gamma / 10); // -90 to 90, divide for subtle effect
        setTiltY(event.beta / 10); // -180 to 180
      }
    };
    
    window.addEventListener('deviceorientation', handleOrientation);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  }, [index, isActive]);
  
  // Progress percentages
  const slotPercentage = (carrier.slots / 1000) * 100;
  const slotStatus = slotPercentage > 70 ? 'high' : slotPercentage > 30 ? 'medium' : 'low';
  
  // Handle tap interaction
  const handleTap = () => {
    triggerHaptic('medium');
    onActivate(carrier.id);
  };
  
  // Carrier icon mapper
  const getIconComponent = () => {
    switch(carrier.id) {
      case 'verizon':
        return (
          <div className="carrier-icon red">
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13 2L3 14h9l-1 8l10-12h-9l1-8z"></path>
            </svg>
          </div>
        );
      case 'att':
        return (
          <div className="carrier-icon blue">
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
            </svg>
          </div>
        );
      case 'google-fi':
        return (
          <div className="carrier-icon light-blue">
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12.55a11 11 0 0 1 14.08 0"></path>
              <path d="M1.42 9a16 16 0 0 1 21.16 0"></path>
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
              <line x1="12" y1="20" x2="12.01" y2="20"></line>
            </svg>
          </div>
        );
      case 'cricket':
        return (
          <div className="carrier-icon green">
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <line x1="21" y1="10" x2="3" y2="10"></line>
              <line x1="21" y1="6" x2="3" y2="6"></line>
              <line x1="21" y1="14" x2="3" y2="14"></line>
              <line x1="21" y1="18" x2="3" y2="18"></line>
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <motion.div 
      ref={cardRef}
      className={`mobile-carrier-card ${carrier.color} ${isActive ? 'active' : ''} ${isFocused ? 'focused' : ''}`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ 
        opacity: 1, 
        scale: isFocused ? 1 : 0.9,
        y: 0,
        rotateX: tiltY,
        rotateY: tiltX,
        boxShadow: isActive 
          ? '0 20px 40px rgba(0, 0, 0, 0.2), 0 0 30px rgba(255, 255, 255, 0.05) inset'
          : '0 10px 30px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05)'
      }}
      transition={{ 
        duration: 0.5, 
        delay: index * 0.1,
        type: 'spring',
        stiffness: 100,
        damping: 15
      }}
      whileTap={{ 
        scale: 0.98,
      }}
      onClick={handleTap}
    >
      <div className="card-content">
        {getIconComponent()}
        
        <h3 className="carrier-name">{carrier.name} Plans</h3>
        
        <div className="color-bar">
          <div className={`color-indicator ${carrier.color}`}></div>
        </div>
        
        <div className="slot-indicator">
          <motion.div 
            className={`slot-bar ${slotStatus}`}
            initial={{ width: 0 }}
            animate={{ width: `${slotPercentage}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        </div>
        
        <p className="slot-info">
          <motion.span 
            className="slot-number"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 + 0.5, duration: 0.5 }}
          >
            {carrier.slots}+
          </motion.span>
          <span className="slot-text">Available Spots</span>
          <Info className="info-icon" size={14} />
        </p>
        
        <div className="deal-container">
          <motion.p 
            className="carrier-deal"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 + 0.3, duration: 0.5 }}
          >
            {carrier.deal}
          </motion.p>
        </div>
        
        <div className="price-info">
          <span className="price-label">Starting from:</span>
          <motion.span 
            className="price"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ delay: index * 0.1 + 0.4, type: 'spring' }}
          >
            ${carrier.minPrice}
          </motion.span>
        </div>
        
        <motion.button 
          className="view-plans-btn"
          whileTap={{ scale: 0.95 }}
          onClick={(e) => {
            e.stopPropagation();
            triggerHaptic('light');
            window.location.href = `/all-circles?carrier=${encodeURIComponent(carrier.name)}`;
          }}
        >
          Explore Plans <ChevronRight className="btn-icon" size={16} />
          <div className="btn-shine"></div>
        </motion.button>
        
        {carrier.isPopular && (
          <motion.div 
            className="popular-tag"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 + 0.6, duration: 0.5 }}
          >
            Top Choice
          </motion.div>
        )}
      </div>
      
      <div className="card-glow"></div>
      <div className="card-reflection"></div>
    </motion.div>
  );
};

// Carousel for carriers (modern 2025 swipe experience)
const CarrierCarousel = ({ carriers, showAllPlans }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const filteredCarriers = carriers.filter(carrier => showAllPlans || carrier.isPopular);
  const [activeCarrier, setActiveCarrier] = useState(null);
  const carouselRef = useRef(null);
  const dragStartX = useRef(0);
  
  // For AR overlay view (2025 feature)
  const [showARView, setShowARView] = useState(false);
  
  // Mobile swipe interaction
  const dragX = useMotionValue(0);
  const dragOpacity = useTransform(
    dragX,
    [-200, 0, 200],
    [0.5, 1, 0.5]
  );
  
  // Handle swipe navigation
  const handleDragEnd = (e, { offset }) => {
    if (offset.x < -70) {
      // Swiped left
      if (currentIndex < filteredCarriers.length - 1) {
        triggerHaptic('light');
        setDirection(1);
        setCurrentIndex(prev => prev + 1);
      } else {
        // Elastic bounce back at end of carousel
        triggerHaptic('medium');
      }
    } else if (offset.x > 70) {
      // Swiped right
      if (currentIndex > 0) {
        triggerHaptic('light');
        setDirection(-1);
        setCurrentIndex(prev => prev - 1);
      } else {
        // Elastic bounce back at start of carousel
        triggerHaptic('medium');
      }
    }
    dragX.set(0);
  };
  
  // Navigate via buttons
  const handleNext = () => {
    if (currentIndex < filteredCarriers.length - 1) {
      triggerHaptic('light');
      setDirection(1);
      setCurrentIndex(prev => prev + 1);
    }
  };
  
  const handlePrev = () => {
    if (currentIndex > 0) {
      triggerHaptic('light');
      setDirection(-1);
      setCurrentIndex(prev => prev - 1);
    }
  };
  
  const handleCarrierActivation = (id) => {
    setActiveCarrier(activeCarrier === id ? null : id);
  };
  
  return (
    <div className="carrier-carousel-container">
      <motion.div 
        className="carousel-navigation"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <button 
          className={`nav-button prev ${currentIndex === 0 ? 'disabled' : ''}`}
          onClick={handlePrev}
          disabled={currentIndex === 0}
          aria-label="Previous carrier"
        >
          <ChevronLeft size={20} />
        </button>
        
        <div className="carousel-indicators">
          {filteredCarriers.map((_, i) => (
            <button 
              key={i} 
              className={`indicator ${i === currentIndex ? 'active' : ''}`}
              onClick={() => {
                triggerHaptic('light');
                setDirection(i > currentIndex ? 1 : -1);
                setCurrentIndex(i);
              }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
        
        <button 
          className={`nav-button next ${currentIndex === filteredCarriers.length - 1 ? 'disabled' : ''}`}
          onClick={handleNext}
          disabled={currentIndex === filteredCarriers.length - 1}
          aria-label="Next carrier"
        >
          <ChevronRight size={20} />
        </button>
      </motion.div>
      
      <motion.div 
        className="carousel-viewport"
        ref={carouselRef}
        style={{ opacity: dragOpacity }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.1}
        onDragStart={() => dragStartX.current = dragX.get()}
        onDragEnd={handleDragEnd}
        whileTap={{ cursor: 'grabbing' }}
      >
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.div
            key={currentIndex}
            custom={direction}
            initial={{ 
              opacity: 0,
              x: direction > 0 ? 300 : -300
            }}
            animate={{ 
              opacity: 1,
              x: 0,
              scale: 1,
              transition: {
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }
            }}
            exit={{ 
              opacity: 0,
              x: direction > 0 ? -300 : 300,
              transition: {
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            style={{ x: dragX }}
            className="carousel-slide"
          >
            {filteredCarriers[currentIndex] && (
              <MobileCarrierCard 
                carrier={filteredCarriers[currentIndex]} 
                index={0}
                isActive={activeCarrier === filteredCarriers[currentIndex].id}
                onActivate={handleCarrierActivation}
                isFocused={true}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </motion.div>
      
      {/* 2025 Feature: AR View Overlay */}
      {showARView && (
        <motion.div 
          className="ar-view-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <button 
            className="close-ar-btn"
            onClick={() => {
              triggerHaptic('medium');
              setShowARView(false);
            }}
          >
            <X size={24} />
          </button>
          <div className="ar-content">
            <div className="ar-guide-text">Point camera at your surroundings</div>
            <div className="ar-mock-interface">
              <div className="ar-circle"></div>
              <div className="ar-pricing-overlay">
                <span className="ar-savings-text">Projected savings: $420/year</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Share button removed as requested */}
    </div>
  );
};

// Mobile-optimized Create Circle Card
const MobileCreateCircleCard = () => {
  const navigate = useNavigate();
  
  // Pulse animation for call-to-action
  const pulseAnimation = {
    scale: [1, 1.05, 1],
    boxShadow: [
      '0 0 0 0 rgba(255, 51, 102, 0.7)',
      '0 0 0 10px rgba(255, 51, 102, 0)',
      '0 0 0 0 rgba(255, 51, 102, 0)'
    ]
  };
  
  return (
    <motion.div 
      className="create-circle-card"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.5 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="glow-effect"></div>
      
      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
      >
        Create Your Own Circle
      </motion.h3>
      
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        Start saving with your custom group plan
      </motion.p>
      
      <motion.div 
        className="circle-btn-container"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <motion.button 
          className="create-circle-btn"
          animate={pulseAnimation}
          transition={{ 
            duration: 2, 
            repeat: Infinity,
            repeatType: "loop" 
          }}
          onClick={() => {
            triggerHaptic('strong');
            navigate('/onboarding');
          }}
        >
          <PlusCircle size={28} />
        </motion.button>
        
        <div className="button-particles">
          {Array.from({ length: 5 }).map((_, i) => (
            <motion.div
              key={i}
              className="button-particle"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ 
                scale: [0, 1, 0],
                opacity: [0, 0.5, 0],
                x: [0, (i % 2 ? 1 : -1) * (Math.random() * 40 + 20)],
                y: [0, (i % 2 ? 1 : -1) * (Math.random() * 40 + 20)]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "loop",
                delay: i * 0.3,
                ease: "easeOut"
              }}
            />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Mobile-optimized Testimonials with swipe navigation
const MobileTestimonials = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [direction, setDirection] = useState(0);
  const swipeThreshold = 50;
  const dragX = useMotionValue(0);
  
  const testimonials = [
    { id: 1, text: "Great way to get premium plans at a fraction of the cost. Customer service was helpful throughout the process.", author: "Emily R." },
    { id: 2, text: "I saved $30 a month on my phone bill with Circledin! Joining was easy and the service has been fantastic.", author: "Sarah K." },
    { id: 3, text: "I never thought group plans could be so simple. Circledin made it happen and saved me hundreds of dollars a year!", author: "Alex P." },
    { id: 4, text: "The customer service is top-notch. They helped me find the perfect circle for my needs and budget.", author: "Laura M." },
    { id: 5, text: "Joining a circle was easy and the savings are real. Highly recommend for anyone looking to cut their phone bill!", author: "Mike T." }
  ];

  // Auto-advance with pause on interaction
  useEffect(() => {
    const interval = setInterval(() => {
      setDirection(1);
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Handle swipe interaction
  const handleDragEnd = (e, { offset }) => {
    if (offset.x < -swipeThreshold) {
      // Swiped left (next)
      triggerHaptic('light');
      setDirection(1);
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    } else if (offset.x > swipeThreshold) {
      // Swiped right (previous)
      triggerHaptic('light');
      setDirection(-1);
      setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    }
  };

  return (
    <motion.div 
      className="mobile-testimonial-container"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.5 }}
    >
      <div className="quote-mark">"</div>
      
      <motion.div
        className="testimonial-swipe-container"
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.2}
        onDragEnd={handleDragEnd}
        style={{ x: dragX }}
      >
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.div 
            key={testimonials[currentTestimonial].id}
            custom={direction}
            className="testimonial-content"
            initial={{ 
              opacity: 0, 
              x: direction > 0 ? 100 : -100
            }}
            animate={{ 
              opacity: 1, 
              x: 0,
              transition: {
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }
            }}
            exit={{ 
              opacity: 0, 
              x: direction > 0 ? -100 : 100,
              transition: {
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }
            }}
          >
            <p className="testimonial-text">{testimonials[currentTestimonial].text}</p>
            <div className="testimonial-author">
              <div className="author-glow"></div>
              <span>- {testimonials[currentTestimonial].author}</span>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>
      
      <div className="testimonial-nav">
        {testimonials.map((_, i) => (
          <motion.button
            key={i}
            className={`nav-dot ${i === currentTestimonial ? 'active' : ''}`}
            onClick={() => {
              triggerHaptic('light');
              setDirection(i > currentTestimonial ? 1 : -1);
              setCurrentTestimonial(i);
            }}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          />
        ))}
      </div>
    </motion.div>
  );
};

// Main Mobile Component
const AvailablePlans_mobile = () => {
  const [showAllPlans, setShowAllPlans] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const navigate = useNavigate();
  
  // Carrier data (same as desktop)
  const carriers = [
    { 
      id: "verizon", 
      name: 'Verizon', 
      deal: 'Up to $540 bonus credit, plans from $25/mo', 
      minPrice: 25, 
      slots: 780, 
      color: 'red', 
      isPopular: true 
    },
    { 
      id: "att", 
      name: 'AT&T', 
      deal: 'No activation fee ($35 value) for new lines', 
      minPrice: 30, 
      slots: 430, 
      color: 'blue' 
    },
    { 
      id: "google-fi", 
      name: 'Google Fi', 
      deal: 'Earn $60 for referring new members', 
      minPrice: 20, 
      slots: 210, 
      color: 'light-blue' 
    },
    { 
      id: "cricket", 
      name: 'Cricket', 
      deal: 'Flexible plans, no annual commitments', 
      minPrice: 20, 
      slots: 150, 
      color: 'green' 
    },
  ];
  
  // Detect section visibility for animations
  useEffect(() => {
    // Check if IntersectionObserver is available
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        },
        { threshold: 0.1 }
      );
      
      if (sectionRef.current) {
        observer.observe(sectionRef.current);
      }
      
      return () => {
        if (sectionRef.current) {
          observer.unobserve(sectionRef.current);
        }
      };
    } else {
      // Fallback for browsers without IntersectionObserver
      setIsVisible(true);
    }
  }, []);
  
  // Toggle between all plans and featured plans
  const togglePlans = (showAll) => {
    triggerHaptic('light');
    setShowAllPlans(showAll);
  };
  
  // Handle exploring all circles
  const handleExploreAllCircles = () => {
    triggerHaptic('medium');
    navigate('/all-circles');
  };
  
  // Dynamic background particles
  const renderParticles = () => {
    return Array.from({ length: 15 }).map((_, i) => (
      <div 
        key={i}
        className="background-particle"
        style={{
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          width: `${Math.random() * 3 + 2}px`,
          height: `${Math.random() * 3 + 2}px`,
          animationDelay: `${Math.random() * 5}s`,
          animationDuration: `${Math.random() * 10 + 10}s`
        }}
      />
    ));
  };

  return (
    <section className="mobile-plans-section" ref={sectionRef}>
      <div className="background-particles">
        {renderParticles()}
      </div>
      <div className="gradient-overlay"></div>
      
      <div className="section-inner">
        <motion.div 
          className="mobile-plans-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="animated-heading">
            <span className="gradient-text">Unlock Premium Plans</span> at Group Rates
          </h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            Join a circle or create your own to access exclusive discounts
          </motion.p>
          
          <motion.button 
            className="explore-all-btn"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.9 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleExploreAllCircles}
          >
            Explore All Circles <ChevronRight size={16} />
          </motion.button>
        </motion.div>

        <motion.div 
          className="plans-toggle-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <button
            className={`toggle-option ${showAllPlans ? 'active' : ''}`}
            onClick={() => togglePlans(true)}
          >
            <Menu size={14} /> All Plans
          </button>
          <button
            className={`toggle-option ${!showAllPlans ? 'active' : ''}`}
            onClick={() => togglePlans(false)}
          >
            <Star size={14} /> Featured Plans
          </button>
          <div className={`toggle-slider ${!showAllPlans ? 'right' : ''}`}></div>
        </motion.div>

        {/* Carrier carousel for mobile swipe experience */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: isVisible ? 1 : 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <CarrierCarousel carriers={carriers} showAllPlans={showAllPlans} />
        </motion.div>
        
        {/* Bottom section with create circle + testimonials */}
        <div className="mobile-bottom-section">
          <MobileCreateCircleCard />
          <MobileTestimonials />
        </div>
      </div>
      
      {/* Floating action button removed as requested */}
    </section>
  );
};

export default AvailablePlans_mobile;